import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineDownload, AiOutlinePlus, AiOutlineReload } from 'react-icons/ai';
import { FiChevronDown, FiCopy, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/shadcn/card-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Label } from '~/components/shadcn/label-simple';
import { Select } from '~/components/shadcn/select-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import api from '~/services/api';
import copyTextToClipboard from '~/utils/copyTextToClipboard';

import { CategoryOption, ItemProps, Root2 } from './types';

function csvEscape(value: string | number | null | undefined) {
  const safeValue = value ?? '';
  const asString = String(safeValue);

  if (asString.includes(',') || asString.includes('"') || asString.includes('\n')) {
    return `"${asString.replace(/"/g, '""')}"`;
  }

  return asString;
}

function exportRowsToCsv(rows: Root2[], fileName: string) {
  const headers = ['ID', 'Brand', 'Category', 'Part Number', 'SKU', 'Name', 'Description', 'CPU', 'Screen Size', 'Screen Type'];

  const lines = rows.map((row) =>
    [
      row.id,
      row.brand?.name ?? '',
      row.category?.name ?? '',
      row.part_number,
      row.sku,
      row.name,
      row.description,
      row.cpu?.name ?? '',
      row.screensize?.name ?? '',
      row.screentype?.name ?? '',
    ]
      .map(csvEscape)
      .join(',')
  );

  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = href;
  anchor.setAttribute('download', fileName);
  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

function getSortDirectionLabel(direction: false | 'asc' | 'desc') {
  if (direction === 'asc') return 'ASC';
  if (direction === 'desc') return 'DESC';
  return '';
}

function parseUsersAllowed(rawValue: string) {
  return String(rawValue || '')
    .split(/[;,]/)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function Item({ location }: ItemProps) {
  const profile = useSelector((state: any) => state.user.profile);
  const [items, setItems] = useState<Root2[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [appliedCategoryId, setAppliedCategoryId] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [canManageItems, setCanManageItems] = useState(false);

  const hasActiveFilters = useMemo(() => appliedSearch.trim().length > 0 || appliedCategoryId !== null, [appliedCategoryId, appliedSearch]);

  const columns = useMemo<ColumnDef<Root2>[]>(
    () => [
      {
        id: 'brand',
        accessorFn: (row) => row.brand?.name ?? '',
        header: 'Brand',
        size: 160,
        minSize: 120,
        maxSize: 240,
      },
      {
        id: 'category',
        accessorFn: (row) => row.category?.name ?? '',
        header: 'Category',
        size: 160,
        minSize: 120,
        maxSize: 240,
      },
      {
        accessorKey: 'part_number',
        header: 'Part Number',
        size: 180,
        minSize: 140,
        maxSize: 280,
        cell: ({ row }) => {
          const partNumber = row.original.part_number;

          const handleCopyPartNumber = async () => {
            const copied = await copyTextToClipboard(partNumber);

            if (copied) {
              toast.success('Part number copied');
            } else {
              toast.error('Unable to copy part number');
            }
          };

          return (
            <div className="flex items-center gap-2">
              <span>{partNumber}</span>
              <button
                type="button"
                onClick={handleCopyPartNumber}
                aria-label={`Copy part number ${partNumber}`}
                title="Copy part number"
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <FiCopy className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        size: 140,
        minSize: 110,
        maxSize: 220,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 180,
        minSize: 130,
        maxSize: 280,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 380,
        minSize: 200,
        maxSize: 700,
        cell: ({ row }) => {
          const description = row.original.description ?? '';
          return <span title={description}>{description}</span>;
        },
      },
      {
        id: 'cpu',
        accessorFn: (row) => row.cpu?.name ?? '',
        header: 'CPU',
        size: 170,
        minSize: 120,
        maxSize: 260,
      },
      {
        id: 'screensize',
        accessorFn: (row) => row.screensize?.name ?? '',
        header: 'Screen Size',
        size: 150,
        minSize: 120,
        maxSize: 230,
      },
      {
        id: 'screentype',
        accessorFn: (row) => row.screentype?.name ?? '',
        header: 'Screen Type',
        size: 150,
        minSize: 120,
        maxSize: 230,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 210,
        minSize: 180,
        maxSize: 280,
        enableSorting: false,
        cell: ({ row }) => {
          const query = appliedSearch ? `?searchQuery=${encodeURIComponent(appliedSearch)}` : '';
          const viewTo = `/items/${row.original.id}/view${query}`;
          const editTo = `/items/${row.original.id}/edit${query}`;

          return (
            <div className="flex items-center gap-2">
              <Link to={viewTo} className="inline-flex">
                <Button type="button" variant="outline" size="sm" className="h-8 border-border/70 bg-background">
                  View
                </Button>
              </Link>
              {canManageItems && (
                <Link to={editTo} className="inline-flex">
                  <Button type="button" variant="outline" size="sm" className="h-8 border-border/70 bg-background">
                    Edit
                  </Button>
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    [appliedSearch, canManageItems]
  );

  const table = useReactTable({
    data: items,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredRowsForCsv = table.getFilteredRowModel().rows.map((row) => row.original);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get<CategoryOption[]>('categories');
      setCategories(response.data);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load categories.';
      toast.error(message);
    }
  }, []);

  const fetchManagePermission = useCallback(async () => {
    try {
      const { data } = await api.get('params?service=Items&key_param=usersAllowed');
      const allowedUsers = parseUsersAllowed(data);
      setCanManageItems(allowedUsers.includes(String(profile?.id)));
    } catch {
      setCanManageItems(false);
    }
  }, [profile?.id]);

  const fetchItems = useCallback(async () => {
    const normalizedSearch = appliedSearch.trim();
    if (!normalizedSearch && appliedCategoryId === null) {
      setItems([]);
      setErrorMessage('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const params: { search?: string; category_id?: number } = {};

      if (normalizedSearch) {
        params.search = normalizedSearch;
      }

      if (appliedCategoryId !== null) {
        params.category_id = appliedCategoryId;
      }

      const response = await api.get<Root2[]>('items', { params });

      if ((response.data as any)?.error) {
        toast.warn('Item has not been found!');
        setItems([]);
        return;
      }

      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load items.';
      setErrorMessage(message);
      toast.error(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [appliedCategoryId, appliedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('searchQuery') ?? '';
    const categoryQuery = params.get('category_id');

    setSearchText(searchQuery);
    setAppliedSearch(searchQuery);

    if (categoryQuery !== null) {
      const parsed = Number(categoryQuery);
      if (!Number.isNaN(parsed)) {
        setSelectedCategory(String(parsed));
        setAppliedCategoryId(parsed);
      }
    }
  }, [location.search]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchManagePermission();
  }, [fetchManagePermission]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const applySearch = useCallback(() => {
    const normalized = searchText.trim();

    setAppliedSearch(normalized);
  }, [searchText]);

  const handleCategoryChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setSelectedCategory(value);

    if (value === 'all') {
      setAppliedCategoryId(null);
      return;
    }

    const parsed = Number(value);
    setAppliedCategoryId(Number.isNaN(parsed) ? null : parsed);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchText('');
    setAppliedSearch('');
    setSelectedCategory('all');
    setAppliedCategoryId(null);
    setSorting([]);
  }, []);

  return (
    <div style={{ backgroundColor: 'var(--white, #ffffff)', height: '100vh', padding: '75px 20px 0 250px', overflow: 'auto' }}>
      <main className="space-y-5">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl font-semibold tracking-tight">Items</h1>
            {canManageItems && (
              <Link to="/items/new" className="inline-flex">
                <Button variant="default" className="gap-2">
                  <AiOutlinePlus className="h-4 w-4" />
                  Add
                </Button>
              </Link>
            )}
          </div>
        </section>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-base">Filters and Table Controls</CardTitle>
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                className={hasActiveFilters ? 'opacity-100' : 'pointer-events-none opacity-0'}
              >
                Clear Filters
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <div className="grid gap-4 lg:grid-cols-12">
              <div className="flex w-full flex-col gap-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-4">
                <Label htmlFor="item-search">Search</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="item-search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') applySearch();
                    }}
                    placeholder="Type at least 3 characters"
                  />
                  <Button type="button" variant="default" onClick={applySearch} className="shrink-0 px-3 shadow-sm">
                    <FiSearch className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-4">
                <Label htmlFor="category-filter">Category</Label>
                <Select id="category-filter" value={selectedCategory} onChange={handleCategoryChange} className="border-border/70 bg-background">
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={String(category.id)}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="relative flex w-full flex-col gap-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-4">
                <Label htmlFor="column-visibility-trigger">Columns and Export</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    id="column-visibility-trigger"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumnMenu((current) => !current)}
                    className="gap-1 border-border/70 bg-background"
                  >
                    <FiChevronDown className="h-4 w-4" />
                    Columns
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => exportRowsToCsv(filteredRowsForCsv, `items-${new Date().toISOString().slice(0, 10)}.csv`)}
                    className="gap-2 border-border/70 bg-background"
                    disabled={filteredRowsForCsv.length === 0}
                  >
                    <AiOutlineDownload className="h-4 w-4" />
                    CSV
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={fetchItems} className="gap-2 border-border/70 bg-background">
                    <AiOutlineReload className="h-4 w-4" />
                    Refresh
                  </Button>
                </div>
                {showColumnMenu && (
                  <div className="max-h-48 space-y-2 overflow-auto rounded-md border border-border/70 bg-background p-3">
                    {table
                      .getAllLeafColumns()
                      .filter((column) => column.id !== 'select')
                      .map((column) => (
                        <label key={column.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={column.getIsVisible()}
                            onChange={(event) => column.toggleVisibility(event.target.checked)}
                            className="h-4 w-4"
                          />
                          <span>{typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}</span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-sm">
          <CardContent className="p-0">
            {loading && (
              <div className="space-y-3 p-4">
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              </div>
            )}

            {!loading && errorMessage && <div className="p-6 text-center text-sm text-destructive">{errorMessage}</div>}

            {!loading && !errorMessage && table.getRowModel().rows.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {hasActiveFilters ? 'No items found for the selected filters.' : 'Use the search box or category filter to list items.'}
              </div>
            )}

            {!loading && !errorMessage && table.getRowModel().rows.length > 0 && (
              <div className="max-h-[calc(100vh-18rem)] overflow-auto">
                <Table className="min-w-[1200px]">
                  <TableHeader className="sticky top-0 z-10 border-b bg-muted/50 backdrop-blur-sm">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} style={{ width: `${header.getSize()}px` }} className="relative whitespace-nowrap py-3">
                            {!header.isPlaceholder && (
                              <button
                                type="button"
                                className={
                                  header.column.getCanSort()
                                    ? 'flex items-center gap-2 text-left text-[13px] font-semibold tracking-wide text-foreground/90'
                                    : 'text-left text-[13px] font-semibold tracking-wide text-foreground/90'
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="text-xs text-muted-foreground">{getSortDirectionLabel(header.column.getIsSorted())}</span>
                                )}
                              </button>
                            )}
                            {header.column.getCanResize() && (
                              <button
                                type="button"
                                aria-label={`Resize ${String(header.column.columnDef.header ?? header.column.id)} column`}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none bg-transparent hover:bg-primary/40"
                              />
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} className="border-b border-border/60 transition-colors hover:bg-muted/40">
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} style={{ width: `${cell.column.getSize()}px` }} className="align-top">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
